import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTheme } from "next-themes";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  DEFAULT_PIECE_THEME,
  isValidPieceTheme,
  loadLocalPieceTheme,
  saveLocalPieceTheme,
} from "../game/pieceThemes";
import { useAuth } from "./AuthContext";

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const { setTheme } = useTheme();
  const [pieceTheme, setPieceThemeState] = useState(() => loadLocalPieceTheme());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) return;
    if (user && user.preferences?.piece_theme && isValidPieceTheme(user.preferences.piece_theme)) {
      setPieceThemeState(user.preferences.piece_theme);
      saveLocalPieceTheme(user.preferences.piece_theme);
    }
    if (user?.preferences?.ui_theme && ["dark", "light", "system"].includes(user.preferences.ui_theme)) {
      setTheme(user.preferences.ui_theme);
    } else if (user === false) {
      setPieceThemeState(loadLocalPieceTheme());
    }
  }, [user, setTheme]);

  const setUiTheme = useCallback(
    async (next) => {
      if (!["dark", "light", "system"].includes(next)) return;
      setTheme(next);
      setError("");
      if (user?.id) {
        setSaving(true);
        try {
          await api.patch("/auth/preferences", { ui_theme: next });
          await refreshMe();
        } catch (e) {
          const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
          setError(msg);
          throw new Error(msg);
        } finally {
          setSaving(false);
        }
      }
    },
    [user, setTheme, refreshMe]
  );

  const setPieceTheme = useCallback(
    async (nextId) => {
      if (!isValidPieceTheme(nextId)) return;
      setPieceThemeState(nextId);
      saveLocalPieceTheme(nextId);
      setError("");

      if (user && user.id) {
        setSaving(true);
        try {
          const { data } = await api.patch("/auth/preferences", { piece_theme: nextId });
          if (data?.preferences?.piece_theme) {
            setPieceThemeState(data.preferences.piece_theme);
          }
          await refreshMe();
        } catch (e) {
          const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
          setError(msg);
          throw new Error(msg);
        } finally {
          setSaving(false);
        }
      }
    },
    [user, refreshMe]
  );

  const value = useMemo(
    () => ({ pieceTheme, setPieceTheme, setUiTheme, saving, error }),
    [pieceTheme, setPieceTheme, setUiTheme, saving, error]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    return {
      pieceTheme: loadLocalPieceTheme() || DEFAULT_PIECE_THEME,
      setPieceTheme: async (id) => saveLocalPieceTheme(id),
      saving: false,
      error: "",
    };
  }
  return ctx;
}
